import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import styles from './ContactForm.module.scss';
import { Button, Title, Group, RadioGroup, Radio, Textarea, Select, TextInput, LoadingOverlay, Text, Alert, Checkbox } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from "use-debounce";
import { store } from '../../redux/store';
import { appActions, selectContactState, selectCurrentEmail, selectCountry, selectFrontlineCountries, roles, titles, usStates, caTerritories, countryList, selectDomainCountry } from '../../redux/appSlice';
import chevronDown from '../../assets/icons/chevron-down.svg'
import useStyles from '../../hooks/useStyles';
import useTranslation from '../../hooks/useTranslation';
import { sendForm, sendFormGeneric } from '../../services/api'
import { useForm } from '@mantine/form';
import { sortCountries } from '../../utils/general-utils'
import { InfoCircleRed } from '../icons/Icons';

type Props = {
  executeRecaptcha: any,
  children?: React.ReactNode,
  onCancel?: MouseEventHandler,
  onSuccess?: Function,
  hasBeenShared?: boolean,
}

const LS_EMAIL = "rememberEmail"

function ContactForm({ executeRecaptcha, onCancel, onSuccess, children, hasBeenShared = false }: Props) {

  const dispatch = useDispatch()
  const contactState = useSelector(selectContactState)
  const currentEmail = useSelector(selectCurrentEmail)
  const currentCountry = useSelector(selectCountry)
  const frontlines = useSelector(selectFrontlineCountries)
  const domainCountry = useSelector(selectDomainCountry)

  const [sendingState, setSendingState] = useState('not-ready')

  const { classes } = useStyles()
  const t = useTranslation()

  const usStateOptions = usStates.map(item => {
    return {
      label: t(`state-${item.value}`),
      value: item.value
    }
  })

  const provinceTerritoryOptions = caTerritories.map(item => {
    return {
      label: t(`province-territory-${item.value}`),
      value: item.value
    }

  })

  const countryOptions = sortCountries(countryList, t).map((item: any) => {
    return {
      value: item.name,
      label: t('country-' + item.alpha3)
    }
  })

  const frontlineCountryOptions = sortCountries(frontlines, t).map((item: any) => {
    return {
      value: item.name,
      label: t('country-' + item.alpha3)
    }
  })

  const debounced = useDebouncedCallback((target: any, cTarget: any, key: string) => {
    target.currentTarget = cTarget
    form.getInputProps(key as keyof typeof form.values).onChange(target)
  }, 300)

  const initialEmail = currentEmail !== '' ? currentEmail : localStorage.getItem(LS_EMAIL) || ''

  const form = useForm({
    initialValues: {
      help: 'quote',
      helpMessage: '',
      business: '',
      country: '',
      projectCity: '',
      projectName: '',
      email: initialEmail,
      // email: currentEmail !== '' ? currentEmail : localStorage.getItem(LS_EMAIL) || '',
      firstName: '',
      lastName: '',
      phone: '',
      company: '',
      address: '',
      location: currentCountry ? currentCountry : '',
      state: '',
      city: '',
      zipCode: '',
      remember: !!localStorage.getItem(LS_EMAIL),
    },
    validate: {
      email: (value) => (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) ? null : t('ui-form-invalid-email')),
      // email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      phone: (value) => {
        var re = /^[+][0-9]{1,3}[-\s.]?[(]?[0-9]{1,3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im
        if (re.test(String(value).toLowerCase())) {
          return null
        } else {
          return t('ui-form-invalid-phone-number')
        }
      },

    },
  });

  const showFormValues = () => {
    console.log({ values: form.values })
  }

  const sendContactForm = async (values: any) => {
    setSendingState('sending')
    // showFormValues()

    if (values.remember) {
      localStorage.setItem(LS_EMAIL, values.email)
    } else {
      localStorage.removeItem(LS_EMAIL)
    }

    const { recaptchaToken, recaptchaNumber } = await executeRecaptcha('download_kone_com')

    if (hasBeenShared) {
      sendFormGeneric(store, { ...values, recaptchaToken, recaptchaNumber }).then(result => {
        if (result.status !== 'Error') {
          setSendingState('ready')
          if (onSuccess) {
            onSuccess()
          }
        } else {
          setSendingState('error')
        }
      })

    } else {
      
      sendForm(store, { ...values, recaptchaToken, recaptchaNumber }).then(result => {
        if (result.status !== 'Error') {
          dispatch(appActions.setContactState("ready"))
          dispatch(appActions.setCurrentEmail(values.email))
          dispatch(appActions.setNotifyState("ready"))

          setSendingState('ready')
          if (onSuccess) {
            onSuccess()
          }
        } else {
          dispatch(appActions.setContactState("undone"))
          setSendingState('error')
        }
      })
    }

  }

  const handleFormOnChange = (event: any, key: string) => {
    if (event.currentTarget === document.activeElement) {
      debounced(event, event.currentTarget, key)
    } else {
      form.getInputProps(key as keyof typeof form.values).onChange(event)
    }
  }

  // console.log({initialEmail})
  return (
    <>
      <LoadingOverlay visible={sendingState === 'sending'} />
      <form onSubmit={form.onSubmit((values) => {
        // TODO: Do real implementation
        if (!hasBeenShared) {
          dispatch(appActions.setContactState("waiting"))
          dispatch(appActions.setCurrentEmail(values.email))
        }
        sendContactForm(values)
      })}>
        <Group direction="column" align="stretch" className={styles.ContactForm} pb={56} >
          <Title order={3}>{t('ui-modal-contact-kone-header')}</Title>
          {sendingState === 'error' &&
            <Alert icon={<InfoCircleRed />} color="red" mb={16}>
              {t('ui-alert-failed-to-send-form-general')}
            </Alert>
          }
          <RadioGroup
            orientation="vertical"
            label={t('ui-form-how-help')}
            required
            {...form.getInputProps('help')}
          >
            <Radio value="quote" label={t('ui-form-get-quote')} />
            <Radio value="consultation" label={t('ui-form-get-consultation')} />
          </RadioGroup>
          <Textarea
            placeholder={t('ui-form-additional-details')}
            // {...form.getInputProps('helpMessage')}
            onChange={event => handleFormOnChange(event, 'helpMessage')}
          />

          <Title order={3}>{t('ui-form-project-info')}</Title>
          <Select
            label={t('ui-form-building-type')}
            placeholder={t('ui-form-select')}
            rightSection={<RightSection />}
            required
            data={[{ label: t('ui-form-new-building'), value: 'NEB' }, { label: t('ui-form-existing-building'), value: 'MOD' }]}
            {...form.getInputProps('business')}
          />
          <Select
            label={t('ui-form-country-of-project')}
            placeholder={t('ui-form-select')}
            rightSection={<RightSection />}
            required
            searchable
            data={frontlineCountryOptions ? frontlineCountryOptions : []}
            {...form.getInputProps('country')}
          />
          <TextInput label={t('ui-form-city')}
            onChange={event => handleFormOnChange(event, 'projectCity')}
          //  {...form.getInputProps('projectCity')}
          />
          <TextInput label={t('ui-form-project-name')}
            onChange={event => handleFormOnChange(event, 'projectName')}
          //  {...form.getInputProps('projectName')}
          />
          <Title order={3}>{t('ui-form-your-details')}</Title>
          <Select
            label={t('ui-form-title')}
            placeholder={t('ui-form-select')}
            rightSection={<RightSection />}
            data={titles.map(item => { return { value: item.value, label: t(item.text) } })}
          />
          <Select
            label={t('ui-form-your-role')}
            placeholder={t('ui-form-select')}
            rightSection={<RightSection />}
            required
            data={roles.map(item => { return { value: item.id, label: t(item.name) } })}
          />
          <TextInput label={t('ui-form-first-name')} required
            onChange={event => handleFormOnChange(event, 'firstName')}
            error={form.getInputProps('firstName').error}
          /*           onFocus={event => event?.currentTarget.removeAttribute('readonly')}
                    onBlur={event => event?.currentTarget.setAttribute('readonly','')}
                    readOnly */
          //  {...form.getInputProps('firstName')}
          />
          <TextInput label={t('ui-form-last-name')} required
            onChange={event => handleFormOnChange(event, 'lastName')}
            error={form.getInputProps('lastName').error}
          //  {...form.getInputProps('lastName')}
          />
          <TextInput label={t('ui-form-email')} required /* {...form.getInputProps('email')}  disabled={initialEmail !== '' ? true : false}*/
            defaultValue={form.getInputProps('email').value}
            onChange={event => handleFormOnChange(event, 'email')}
            error={form.getInputProps('email').error}
            
          />
          <TextInput label={t('ui-form-phone')} required
            onChange={event => handleFormOnChange(event, 'phone')}
            error={form.getInputProps('phone').error}
            // {...form.getInputProps('phone')}
            placeholder='+xxxxxxxxxxx'
          />
          <TextInput label={t('ui-form-company')} required
            onChange={event => handleFormOnChange(event, 'company')}
          // {...form.getInputProps('company')}
          />
          <TextInput label={t('ui-form-address')} required
            onChange={event => handleFormOnChange(event, 'address')}
          // {...form.getInputProps('address')}
          />
          <Select
            label={t('ui-form-country')}
            placeholder={t('ui-form-select')}
            searchable
            rightSection={<RightSection />}
            data={countryOptions}
            {...form.getInputProps('location')}
          />
          {form.getInputProps('location').value === 'United States' &&
            <Select
              label={t('ui-form-state')}
              placeholder={t('ui-form-select')}
              searchable
              rightSection={<RightSection />}
              data={usStateOptions}
              {...form.getInputProps('state')}
            />
          }
          {form.getInputProps('location').value === 'Canada' &&
            <Select
              label={t('ui-form-province-or-territory')}
              placeholder={t('ui-form-select')}
              rightSection={<RightSection />}
              searchable
              data={provinceTerritoryOptions}
              {...form.getInputProps('state')}
            />
          }
          <TextInput label={t('ui-form-city')} required
            onChange={event => handleFormOnChange(event, 'city')}
            error={form.getInputProps('city').error}
          // {...form.getInputProps('city')}
          />
          <TextInput label={
            form.getInputProps('location').value === 'United States'
              ? t('ui-form-zip-code')
              : t('ui-form-postal-code')
          } required
            onChange={event => handleFormOnChange(event, 'zipCode')}
            error={form.getInputProps('zipCode').error}
          // {...form.getInputProps('zipCode')}
          />

          <Checkbox
            classNames={{ label: styles.label }}
            label={t('ui-landing-remember-me')}
            checked={form.values.remember}
            {...form.getInputProps('remember')}
          />

          <Text size="sm">
            {t('ui-contact-privacy-statement-1')}
            <a
              tabIndex={-1}
              className="readMore"
              href={domainCountry?.privacyStatement}
              target="_blank"
              rel="noopener noreferrer"
            >
              &nbsp;
              {t('ui-contact-privacy-statement-2')}
            </a>
            {t('ui-contact-privacy-statement-3')}
          </Text>
          <Group position="right" className={styles.stickyBottom} spacing="sm">
            <Button onClick={onCancel} variant="outline" className={classes.formButton}>{t("ui-general-back")}</Button>
            <Button type="submit" className={classes.formButton}>{t("ui-general-send")}</Button>
          </Group>
        </Group>
        <LoadingOverlay visible={contactState === "waiting"} />
      </form>
    </>
  );
}

export default ContactForm;

function RightSection() {
  const ref = useRef<HTMLImageElement>(null)
  useEffect(() => {
    const element = ref.current
    if (element && element.parentElement) {
      element.parentElement.style.pointerEvents = "none"
    }
  }, [])

  return (
    <img
      ref={ref}
      alt="chevron down"
      width={18}
      height={18}
      src={chevronDown}
      style={{ pointerEvents: 'none' }}
    />
  )
}